import { Button, Checkbox, Disclosure } from "@legal-500/legal500-design-system";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  contacts,
  contactRoles,
  offices,
  type Contact,
  type ContactRole,
  type Office,
  type SplitContactResponse,
} from "../../../api/addressBook.service";
import LoadingState from "../../../components/LoadingState";
import { PageHeader } from "../../../components/PageHeader";
import { ROUTES } from "../../../routes";
import { formatDateForApi } from "../../../utils/dateFormatter";
import styles from "../AddressBook.module.css";
import { WizardStepper } from "../components/WizardStepper";
import introStyles from "./WizardIntro.module.css";

type Step = "select" | "name" | "review" | "done";

const STEP_LABELS = ["Select Roles", "New Contact", "Review", "Done"];

function stepToIndex(step: Step): number {
  switch (step) {
    case "select":
      return 0;
    case "name":
      return 1;
    case "review":
      return 2;
    case "done":
      return 3;
  }
}

function buildStepperSteps(currentStep: Step) {
  const currentIndex = stepToIndex(currentStep);
  return STEP_LABELS.map((label, i) => ({
    label,
    status: (i < currentIndex ? "complete" : i === currentIndex ? "active" : "pending") as
      | "complete"
      | "active"
      | "pending",
  }));
}

type NewContactForm = {
  firstName: string;
  lastName: string;
  name: string;
  slug: string;
  email: string;
  jobTitle: string;
};

const EMPTY_FORM: NewContactForm = {
  firstName: "",
  lastName: "",
  name: "",
  slug: "",
  email: "",
  jobTitle: "",
};

export default function ContactSplitWizard() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("select");
  const [source, setSource] = useState<Contact | null>(null);
  const [roles, setRoles] = useState<ContactRole[]>([]);
  const [officesMap, setOfficesMap] = useState<Map<string, Office>>(new Map());
  const [selectedRoleIds, setSelectedRoleIds] = useState<Set<string>>(new Set());
  const [newContactForm, setNewContactForm] = useState<NewContactForm>(EMPTY_FORM);
  const [effectiveDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SplitContactResponse | null>(null);

  useEffect(() => {
    if (!id) return;

    async function loadData() {
      setLoading(true);
      try {
        const [contactRes, rolesRes] = await Promise.all([
          contacts.getById(id!),
          contactRoles.getAll({ contactId: id! }),
        ]);

        setSource(contactRes.data);
        setRoles(rolesRes.data);

        const officeIds = [...new Set(rolesRes.data.map(r => r.officeId).filter(Boolean))];
        const officeResults = await Promise.allSettled(officeIds.map(oid => offices.getById(oid)));
        const map = new Map<string, Office>();
        officeIds.forEach((oid, i) => {
          const res = officeResults[i];
          if (res.status === "fulfilled") {
            map.set(oid, res.value.data);
          }
        });
        setOfficesMap(map);
      } catch {
        setError("Failed to load contact data");
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [id]);

  const activeRoles = useMemo(() => roles.filter(r => r.endDate === null), [roles]);

  const derivedName = useMemo(
    () =>
      [newContactForm.firstName.trim(), newContactForm.lastName.trim()].filter(Boolean).join(" "),
    [newContactForm.firstName, newContactForm.lastName],
  );

  function officeName(officeId: string): string {
    const office = officesMap.get(officeId);
    return office?.name ?? "Unknown office";
  }

  function toggleRole(roleId: string) {
    setSelectedRoleIds(prev => {
      const next = new Set(prev);
      if (next.has(roleId)) {
        next.delete(roleId);
      } else {
        next.add(roleId);
      }
      return next;
    });
  }

  const emailTrimmed = newContactForm.email.trim();
  const emailError =
    emailTrimmed.length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed)
      ? "Invalid email format"
      : null;

  const canProceedFromSelect = selectedRoleIds.size > 0;
  const canProceedFromName =
    newContactForm.firstName.trim().length > 0 &&
    newContactForm.lastName.trim().length > 0 &&
    !emailError;

  async function handleSubmit() {
    if (!source) return;
    setSubmitting(true);
    setError(null);

    try {
      const payloadName = newContactForm.name.trim() || derivedName;
      // The server derives a unique slug from the name when none is sent, so
      // only an explicitly typed slug is forwarded.
      const typedSlug = newContactForm.slug.trim();
      const response = await contacts.split(source.id, {
        newContact: {
          firstName: newContactForm.firstName.trim(),
          lastName: newContactForm.lastName.trim(),
          name: payloadName || undefined,
          ...(typedSlug ? { slug: typedSlug } : {}),
          email: newContactForm.email.trim() || undefined,
          jobTitle: newContactForm.jobTitle.trim() || undefined,
        },
        roleIds: [...selectedRoleIds],
        effectiveDate: effectiveDate ? (formatDateForApi(effectiveDate) ?? null) : null,
      });
      setResult(response);
      setStep("done");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to split contact";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className={styles.wizardWrapper}>
        <PageHeader title="Split Contact" />
        <LoadingState message="Loading contact data..." />
      </div>
    );
  }

  if (!source) {
    return (
      <div className={styles.wizardWrapper}>
        <PageHeader title="Split Contact" />
        <div className={styles.formAlert}>Contact not found.</div>
      </div>
    );
  }

  const newContactDisplayName = derivedName || "new contact";

  return (
    <div className={styles.wizardWrapper}>
      <PageHeader title="Split Contact" subtitle={source.name} />

      <WizardStepper steps={buildStepperSteps(step)} />

      <div className={styles.wizardContent}>
        {step === "select" && (
          <div className={introStyles.intro}>
            <p className={introStyles.oneLiner}>Creates a new contact with a new ID.</p>
            <Disclosure summary="How it works">
              <p className={introStyles.detail}>
                The selected roles are moved from this contact to the new one. The original stays
                active with its remaining roles.
              </p>
            </Disclosure>
          </div>
        )}

        {step === "select" && (
          <SelectStep
            source={source}
            activeRoles={activeRoles}
            selectedRoleIds={selectedRoleIds}
            onToggleRole={toggleRole}
            officeName={officeName}
          />
        )}

        {step === "name" && (
          <NameStep
            source={source}
            selectedCount={selectedRoleIds.size}
            form={newContactForm}
            onChange={setNewContactForm}
            derivedName={derivedName}
            emailError={emailError}
          />
        )}

        {step === "review" && (
          <ReviewStep
            source={source}
            activeRoles={activeRoles}
            selectedRoleIds={selectedRoleIds}
            newContactName={newContactDisplayName}
            form={newContactForm}
            derivedName={derivedName}
            officeName={officeName}
            error={error}
            effectiveDate={effectiveDate}
          />
        )}

        {step === "done" && result && (
          <DoneStep newContactName={result.newContact.name} rolesMoved={result.rolesMoved} />
        )}
      </div>

      <div className={styles.wizardActions}>
        {step === "select" && (
          <>
            <Button variant="outlined" color="default" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <div className={styles.wizardActionsRight}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setStep("name")}
                disabled={!canProceedFromSelect}
              >
                Next
              </Button>
            </div>
          </>
        )}

        {step === "name" && (
          <>
            <Button variant="outlined" color="default" onClick={() => setStep("select")}>
              Back
            </Button>
            <div className={styles.wizardActionsRight}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setStep("review")}
                disabled={!canProceedFromName}
              >
                Next
              </Button>
            </div>
          </>
        )}

        {step === "review" && (
          <>
            <Button variant="outlined" color="default" onClick={() => setStep("name")}>
              Back
            </Button>
            <div className={styles.wizardActionsRight}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? "Splitting..." : "Confirm"}
              </Button>
            </div>
          </>
        )}

        {step === "done" && result && (
          <div className={styles.wizardActionsRight}>
            <Button
              variant="outlined"
              color="default"
              onClick={() => navigate(ROUTES.addressBook.contacts)}
            >
              Back to Contacts
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(ROUTES.addressBook.contactDetail(result.newContact.id))}
            >
              View {result.newContact.name}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

type SelectStepProps = {
  source: Contact;
  activeRoles: ContactRole[];
  selectedRoleIds: Set<string>;
  onToggleRole: (roleId: string) => void;
  officeName: (officeId: string) => string;
};

function SelectStep({
  source,
  activeRoles,
  selectedRoleIds,
  onToggleRole,
  officeName,
}: SelectStepProps) {
  const selectedCount = selectedRoleIds.size;
  const remainingCount = activeRoles.length - selectedCount;
  const allSelected = activeRoles.length > 0 && selectedCount === activeRoles.length;

  return (
    <div className={styles.wizardFormContainer}>
      <div className={`${styles.section} ${styles.sectionAccent}`}>
        <h3 className={styles.sectionTitle}>Source Contact</h3>
        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Name</span>
            <span className={styles.infoValue}>{source.name}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Email</span>
            <span className={styles.infoValue}>
              {source.email || <span className={styles.emptyText}>No email</span>}
            </span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Active roles</span>
            <span className={styles.infoValue}>{activeRoles.length}</span>
          </div>
        </div>
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Roles to Move</h3>
        {activeRoles.length === 0 ? (
          <p className={styles.helperText}>No active roles found.</p>
        ) : (
          <>
            <div className={styles.selectionSummary}>
              {selectedCount} role{selectedCount !== 1 ? "s" : ""} will move, {remainingCount}{" "}
              remain with {source.name}
            </div>
            <div className={`${styles.roleCheckboxList} ${styles.roleCheckboxListSpaced}`}>
              {activeRoles.map(role => {
                const checked = selectedRoleIds.has(role.id);
                return (
                  <div
                    key={role.id}
                    className={`${styles.roleCheckboxItem} ${checked ? styles.roleCheckboxItemSelected : ""}`}
                  >
                    <Checkbox
                      id={`split-role-${role.id}`}
                      checked={checked}
                      onChange={() => onToggleRole(role.id)}
                      label={`${role.roleType || "Role"} — ${officeName(role.officeId)}`}
                    />
                  </div>
                );
              })}
            </div>
            {allSelected && (
              <div className={`${styles.warningBox} ${styles.warningBoxSpaced}`}>
                <strong>Warning:</strong> All active roles will move to the new contact. The source
                contact will have no active roles after the split.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

type NameStepProps = {
  source: Contact;
  selectedCount: number;
  form: NewContactForm;
  onChange: (next: NewContactForm) => void;
  derivedName: string;
  emailError: string | null;
};

function NameStep({
  source,
  selectedCount,
  form,
  onChange,
  derivedName,
  emailError,
}: NameStepProps) {
  function update<K extends keyof NewContactForm>(key: K, value: NewContactForm[K]) {
    onChange({ ...form, [key]: value });
  }

  return (
    <div className={styles.wizardFormContainer}>
      <div className={`${styles.section} ${styles.sectionAccent}`}>
        <p className={styles.infoValue}>
          Splitting <strong>{selectedCount}</strong> role{selectedCount !== 1 ? "s" : ""} from{" "}
          <strong>{source.name}</strong>. Define the new contact.
        </p>
      </div>

      <div className={styles.section}>
        <div className={styles.formField}>
          <label className={styles.formLabel} htmlFor="split-first-name">
            First Name *
          </label>
          <input
            id="split-first-name"
            type="text"
            className={styles.formInput}
            value={form.firstName}
            onChange={e => update("firstName", e.target.value)}
            required
          />
        </div>

        <div className={styles.formField}>
          <label className={styles.formLabel} htmlFor="split-last-name">
            Last Name *
          </label>
          <input
            id="split-last-name"
            type="text"
            className={styles.formInput}
            value={form.lastName}
            onChange={e => update("lastName", e.target.value)}
            required
          />
        </div>

        <div className={styles.formField}>
          <label className={styles.formLabel} htmlFor="split-name">
            Name
          </label>
          <input
            id="split-name"
            type="text"
            className={styles.formInput}
            value={form.name}
            onChange={e => update("name", e.target.value)}
            placeholder={derivedName || "Canonical display name"}
          />
          {!form.name && derivedName ? (
            <span className={styles.helperText}>Will be saved as: {derivedName}</span>
          ) : (
            <span className={styles.helperText}>
              Leave blank to auto-derive from first/last name
            </span>
          )}
        </div>

        <div className={styles.formField}>
          <label className={styles.formLabel} htmlFor="split-slug">
            Slug
          </label>
          <input
            id="split-slug"
            type="text"
            className={styles.formInput}
            value={form.slug}
            onChange={e => update("slug", e.target.value)}
            placeholder="auto-generated-from-name"
          />
          <span className={styles.helperText}>
            Leave blank to generate a unique slug from the name on save
          </span>
        </div>

        <div className={styles.formField}>
          <label className={styles.formLabel} htmlFor="split-email">
            Email
          </label>
          <input
            id="split-email"
            type="email"
            className={`${styles.formInput} ${emailError ? styles.formInputError : ""}`}
            value={form.email}
            onChange={e => update("email", e.target.value)}
            aria-invalid={!!emailError}
            aria-describedby={emailError ? "split-email-error" : undefined}
          />
          {emailError && (
            <span id="split-email-error" className={styles.errorText}>
              {emailError}
            </span>
          )}
        </div>

        <div className={styles.formField}>
          <label className={styles.formLabel} htmlFor="split-job-title">
            Job Title
          </label>
          <input
            id="split-job-title"
            type="text"
            className={styles.formInput}
            value={form.jobTitle}
            onChange={e => update("jobTitle", e.target.value)}
          />
        </div>
      </div>
    </div>
  );
}

type ReviewStepProps = {
  source: Contact;
  activeRoles: ContactRole[];
  selectedRoleIds: Set<string>;
  newContactName: string;
  form: NewContactForm;
  derivedName: string;
  officeName: (officeId: string) => string;
  error: string | null;
  effectiveDate: string;
};

function ReviewStep({
  source,
  activeRoles,
  selectedRoleIds,
  newContactName,
  form,
  derivedName,
  officeName,
  error,
  effectiveDate,
}: ReviewStepProps) {
  const movingRoles = activeRoles.filter(r => selectedRoleIds.has(r.id));
  const remainingRoles = activeRoles.filter(r => !selectedRoleIds.has(r.id));
  const finalName = form.name.trim() || derivedName;
  const finalSlug = form.slug.trim();
  const allSelected = activeRoles.length > 0 && movingRoles.length === activeRoles.length;

  return (
    <div className={styles.wizardFormContainer}>
      {error && <div className={styles.formAlert}>{error}</div>}

      <div className={`${styles.section} ${styles.sectionAccent}`}>
        <h3 className={styles.sectionTitle}>New Contact</h3>
        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>First Name</span>
            <span className={styles.infoValue}>{form.firstName.trim()}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Last Name</span>
            <span className={styles.infoValue}>{form.lastName.trim()}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Name</span>
            <span className={styles.infoValue}>{finalName}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Slug</span>
            <span className={styles.infoValue}>
              {finalSlug || <span className={styles.emptyText}>Generated from name on save</span>}
            </span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Email</span>
            <span className={styles.infoValue}>
              {form.email.trim() || <span className={styles.emptyText}>—</span>}
            </span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoLabel}>Job Title</span>
            <span className={styles.infoValue}>
              {form.jobTitle.trim() || <span className={styles.emptyText}>—</span>}
            </span>
          </div>
        </div>
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Roles moving to {newContactName}</h3>
        {movingRoles.length === 0 ? (
          <p className={styles.helperText}>No roles selected.</p>
        ) : (
          <div className={styles.roleListReview}>
            {movingRoles.map(role => (
              <div key={role.id} className={styles.roleItemReview}>
                {role.roleType || "Role"} — {officeName(role.officeId)}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Remaining with {source.name}</h3>
        {remainingRoles.length === 0 ? (
          <p className={styles.helperText}>No roles remain.</p>
        ) : (
          <div className={styles.roleListReview}>
            {remainingRoles.map(role => (
              <div key={role.id} className={styles.roleItemReview}>
                {role.roleType || "Role"} — {officeName(role.officeId)}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={styles.section}>
        <div className={styles.formField}>
          <label className={styles.formLabel} htmlFor="split-effective-date">
            Effective Date
          </label>
          <input
            id="split-effective-date"
            type="date"
            className={styles.formInput}
            value={effectiveDate}
            disabled
          />
          <span className={styles.helperText}>Date the split is recorded</span>
        </div>
      </div>

      {allSelected && (
        <div className={styles.warningBox}>
          <strong>Warning:</strong> All active roles will move to the new contact. The source
          contact will have no active roles after the split.
        </div>
      )}
    </div>
  );
}

type DoneStepProps = {
  newContactName: string;
  rolesMoved: number;
};

function DoneStep({ newContactName, rolesMoved }: DoneStepProps) {
  return (
    <div className={styles.doneContainer}>
      <div className={styles.doneIconWrapper}>
        <span className={styles.doneIcon}>✓</span>
      </div>
      <h3 className={styles.doneTitle}>Contact Split</h3>
      <p className={styles.doneMessage}>
        {rolesMoved} role{rolesMoved !== 1 ? "s" : ""} transferred to{" "}
        <strong>{newContactName}</strong>
      </p>
    </div>
  );
}
